import { useEffect, useState } from 'react';

import { useCartWithServerOrderSurcharges } from 'features/cart/hooks/use-cart-with-server-order-surcharges';
import { MappedCartEntry, MappedOrder } from 'features/menu/components/menu-reorder-widget/types';

import { IHistoricalOrder, IServerOrder } from '@rbi-ctg/menu';
import { MenuObjectTypes } from 'enums/menu';
import { IImageFragment } from 'generated/sanity-graphql';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { useGetMenuObjectQuery } from 'state/menu/menu-options/hooks/use-get-menu-object-query';
import { useMapToFrontendCartEntry } from 'state/order/hooks/use-map-to-frontend-cart-entry';
import { useUIContext } from 'state/ui';
import logger from 'utils/logger';
import { maybeMapCartEntryToEntryTypeAndIdForDisplay } from 'utils/menu/cart-entries-to-menu-object-id';
import { parseStringifiedJSON } from 'utils/parse-string';

type ItemHighlighted = {
  name: string;
  image: IImageFragment;
};

interface UseOrderDetailsReturn {
  totalPrice: string;
  itemHighlighted: ItemHighlighted | null;
  totalItems: number;
  itemNames: string;
}

function isMappedOrder(order: IServerOrder | IHistoricalOrder): order is MappedOrder {
  return 'orderEntries' in order;
}

export const useOrderDetailsCard = (
  order: IServerOrder | IHistoricalOrder,
  isReorder: boolean = false
): UseOrderDetailsReturn => {
  const { formatCurrencyForLocale } = useUIContext();
  const mapToFrontendEntry = useMapToFrontendCartEntry();

  const {
    cart: { cartEntries },
  } = order;
  const { cartTotalCents } = useCartWithServerOrderSurcharges(order.cart);

  const entries = cartEntries ?? [];
  const orderDetailsEntries: MappedCartEntry[] =
    isReorder && isMappedOrder(order) ? order.orderEntries : [];
  const totalItems = entries.reduce((total, cartEntry) => total + (cartEntry.quantity ?? 1), 0);
  const totalPrice = formatCurrencyForLocale(cartTotalCents);
  const itemNames =
    isReorder && isMappedOrder(order)
      ? orderDetailsEntries.map(item => item.name).join(' - ')
      : entries.map(item => item.name).join(' - ');

  const [itemHighlighted, setItemHighlighted] = useState<ItemHighlighted>({
    name: entries[0]?.name || '',
    image: parseStringifiedJSON({
      value: entries[0]?.image || '',
    }),
  });

  // The highlighted item's name is baked in as a plain string by the server at order-placement
  // time and never re-resolved afterward, so a guest who places an order in one language and
  // later switches the app's language keeps seeing the old language on this card forever. Prefer
  // a fresh, currently-localized Sanity lookup for display — only when it actually resolves, so
  // a lookup miss (e.g. a discontinued item) can't blank out a name that was already correct.
  // For One Menu stores, this legacy Sanity lookup must stay disabled: the same sanityId can
  // still resolve there even though it's not the authoritative source for a One Menu order, and
  // the `useEffect` below already derives the correct name for One Menu via `mapToFrontendEntry`.
  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  const firstEntry = entries[0];
  const { sanityId, menuObjectType } = firstEntry
    ? maybeMapCartEntryToEntryTypeAndIdForDisplay(firstEntry)
    : { sanityId: '', menuObjectType: null };
  const { data: highlightedMenuObject } = useGetMenuObjectQuery({
    sanityId: isUnifiedMenuEnabled ? '' : sanityId,
    menuObjectType: menuObjectType as MenuObjectTypes,
  });
  const freshHighlightedName = highlightedMenuObject?.name?.locale;

  useEffect(() => {
    // For One Menu stores, the image is not included in the order cart entries,
    // so we need to retrieve it by mapping from the frontend cart entry.
    if (!itemHighlighted.image && !!cartEntries?.[0]) {
      mapToFrontendEntry(cartEntries?.[0])
        .then(data => {
          if (!data?.cartEntry?.name || !data?.cartEntry?.image) {
            return;
          }

          setItemHighlighted({
            name: data.cartEntry.name,
            image: data.cartEntry.image as IImageFragment,
          });
        })
        .catch(error => {
          logger.warn({
            error,
            message: `Error mapping reorder cart entry: ${cartEntries?.[0].sanityId}`,
          });
        });
    }
  }, [cartEntries, itemHighlighted.image, mapToFrontendEntry]);

  return {
    totalItems,
    totalPrice,
    itemHighlighted: freshHighlightedName
      ? { ...itemHighlighted, name: freshHighlightedName }
      : itemHighlighted,
    itemNames,
  };
};
